import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { isMobileUA } from 'dpl/shared/utils';
import { sendAnalyticsEvent, sendRudderstackEvent } from 'dpl/util/analytics';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEWS,
  ANALYTICS_VIEW_FEATURES
} from 'dpl/constants/analytics';

import StripeChargeModalPaymentSummaryLineItemV2 from './StripeChargeModalPaymentSummaryLineItemV2';
import { PAYMENT_TIER_TYPE, SELECT_PAYMENT_MODAL_NAME } from './constants';
import ProtectionAndSupportContent from './ProtectionAndSupportContent';
import SelectPaymentTypeParentModal from './SelectPaymentTypeParentModal';
import ProtectionAndSupportTooltip from './ProtectionAndSupportTooltip';

const { BASIC } = PAYMENT_TIER_TYPE;

export default function StripeChargeModalPlatformFeeLineItem(props) {
  const [isProtectionSupportOpen, setIsProtectionSupportOpen] = useState(false);
  const [currentModal, setCurrentModal] = useState(null);
  const isMobile = isMobileUA();

  const isBasicTier = props.paymentTier === BASIC;
  const lineItemDescription = isBasicTier
    ? `No ${props.description}`
    : props.description;
  const longDescription = isBasicTier ? null : props.longDescription;

  function trackEvent(buttonCta) {
    sendRudderstackEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      button_cta: buttonCta,
      view: 'buyer_payment_summary'
    });
  }

  function handleOpenModalClick(modalName) {
    setCurrentModal(modalName);

    if (isMobile) {
      setIsProtectionSupportOpen(false);
    }
  }

  function handleIconClick() {
    if (!isMobile) {
      setCurrentModal(SELECT_PAYMENT_MODAL_NAME.LEARN_MORE);
    } else {
      setIsProtectionSupportOpen(true);
      sendAnalyticsEvent(ANALYTICS_EVENTS.PAYMENT_MODAL_VIEWED_BY_BUYER, {
        view: ANALYTICS_VIEWS.PROTECTION_SUPPORT_QUICK_VIEW,
        view_feature: ANALYTICS_VIEW_FEATURES.PAYMENT_CHECKOUT_PAGE
      });
    }
  }

  function handleClosePaymentTierClick() {
    setCurrentModal(null);
  }

  function handleSavePaymentTierClick(tier) {
    handleClosePaymentTierClick();
    setIsProtectionSupportOpen(false);
    props.onChangeSelection(tier);
  }

  function handleVisibilityChange() {
    trackEvent('icon:protection_and_support_tooltip');
    setCurrentModal(SELECT_PAYMENT_MODAL_NAME.LEARN_MORE);
  }

  return (
    <>
      <StripeChargeModalPaymentSummaryLineItemV2
        {...props}
        description={lineItemDescription}
        longDescription={longDescription}
        iconProps={{
          name: 'fetch-info',
          buttonId: 'platform-fee-button'
        }}
        tooltipProps={{
          title: (
            <ProtectionAndSupportContent
              amountInDollars={props.subtotalInDollars}
              onOpenModalClick={handleOpenModalClick}
              isDogPayment={props.dogPayment}
            />
          ),
          isVisible: isProtectionSupportOpen,
          onClick: handleIconClick,
          onCloseTooltipClick: () => setIsProtectionSupportOpen(false)
        }}
        modalProps={{
          isVisible: isProtectionSupportOpen,
          onVisibilityChanged: handleVisibilityChange,
          content: (
            <ProtectionAndSupportContent
              amountInDollars={props.subtotalInDollars}
              onOpenModalClick={handleOpenModalClick}
              isDogPayment={props.dogPayment}
            />
          )
        }}
        desktopTooltipProps={{
          title: (
            <ProtectionAndSupportTooltip
              amountInDollars={props.subtotalInDollars}
              onOpenModalClick={handleOpenModalClick}
              isDogPayment={props.dogPayment}
            />
          )
        }}
      />
      {currentModal && (
        <SelectPaymentTypeParentModal
          paymentTier={props.paymentTier}
          defaultModalName={currentModal}
          onCloseClick={handleClosePaymentTierClick}
          dogPayment={props.dogPayment}
          amount={props.subtotalInDollars}
          onSaveSelection={handleSavePaymentTierClick}
        />
      )}
    </>
  );
}

StripeChargeModalPlatformFeeLineItem.propTypes = {
  amountInDollars: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  description: PropTypes.string.isRequired,
  longDescription: PropTypes.string,
  discountedPriceInDollars: PropTypes.string,
  subtotalInDollars: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  paymentTier: PropTypes.oneOf(Object.values(PAYMENT_TIER_TYPE)).isRequired,
  dogPayment: PropTypes.bool.isRequired,
  onChangeSelection: PropTypes.func.isRequired,
  disableInteractions: PropTypes.bool
};

StripeChargeModalPlatformFeeLineItem.defaultProps = {
  longDescription: null,
  discountedPriceInDollars: null,
  subtotalInDollars: null,
  disableInteractions: false
};
